import {
  stepsWithPdfTemplates,
  type FormSection,
  type WorkflowFieldType,
  type WorkflowStep,
  type WorkflowTemplate,
} from '@/api/workflows';

/**
 * One ShortText/Number field plus enough breadcrumb info to label it inside the
 * E-Signature drawer's field Selects (so the originator can tell apart "First
 * name" inside Step 2 / Personal Info from "First name" inside Step 4 / Joint
 * Holder Info). Steps + sections are skipped if their bilingual label is empty
 * — the FE never persists an empty label, so this guards legacy seed data.
 */
export type FieldChoice = {
  id: string;
  stepId: string;
  stepLabel: string;
  sectionLabel: string;
  fieldLabel: string;
  type: WorkflowFieldType;
};

const NAME_TYPES: ReadonlyArray<WorkflowFieldType> = ['ShortText'];
const EMAIL_TYPES: ReadonlyArray<WorkflowFieldType> = ['ShortText'];
const PHONE_TYPES: ReadonlyArray<WorkflowFieldType> = ['ShortText', 'Number'];
// The language slot takes only the closed-choice inputs. Unlike the contact
// slots, this one's value has to match a vocabulary the BFF recognizes (`en` /
// `fr`), and a free-text box gives the client no way to know that — a radio or
// dropdown at least puts the originator in control of the stored values.
const LANGUAGE_TYPES: ReadonlyArray<WorkflowFieldType> = ['RadioGroup', 'SingleSelect'];

const localized = (en: string, fr: string, isFr: boolean) => (isFr ? fr : en) || en || fr;

/**
 * Walk every DynamicForm step and collect candidate fields by allowed field
 * type. Today every signer slot accepts ShortText except `phone`, which also
 * accepts Number (BMO's payload schema says `phone: number`). The filter is
 * deliberately loose — we trust the originator to pick a sensible field rather
 * than locking down by `validate` rule.
 */
export function collectFieldChoices(
  template: WorkflowTemplate,
  isFr: boolean,
  allowedTypes: ReadonlyArray<WorkflowFieldType>,
): FieldChoice[] {
  const out: FieldChoice[] = [];
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    const stepLabel = localized(step.titleEn, step.titleFr, isFr);
    for (const section of step.sections) {
      const sectionLabel = localized(section.titleEn, section.titleFr, isFr);
      for (const row of section.rows) {
        for (const column of row.columns) {
          const f = column.field;
          if (!f) continue;
          if (!allowedTypes.includes(f.type)) continue;
          out.push({
            id: f.id,
            stepId: step.id,
            stepLabel,
            sectionLabel,
            fieldLabel: localized(f.labelEn, f.labelFr, isFr),
            type: f.type,
          });
        }
      }
    }
  }
  return out;
}

/**
 * A repeatable section with no authored `maxInstances` has no upper bound at
 * runtime, so the "Signs as" instance picker needs its own display cap.
 * Independent of `pdfFieldMapping.ts`'s identical-valued constant — that
 * module carries a real pdf.js side effect at import time and pulling from it
 * here just for a number would drag it into this drawer's bundle for free.
 */
export const FIXED_INSTANCE_OPTION_LIMIT = 10;

/**
 * fieldId -> owning FormSection, for every authored field on the template.
 *
 * Unlike `@bmo/form-core`'s `buildFieldSections` (which returns just enough to
 * resolve a runtime value key), this keeps the WHOLE section — the E-Signature
 * drawer's "Signs as" instance picker needs `maxInstances` + the bilingual
 * instance-label pattern to render options that match what the advisor sees
 * at runtime (`renderInstanceLabel`).
 */
export function collectSectionsByFieldId(template: WorkflowTemplate): Map<string, FormSection> {
  const out = new Map<string, FormSection>();
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          if (column.field) out.set(column.field.id, section);
        }
      }
    }
  }
  return out;
}

/**
 * The repeatable section a signer role's contact mappings live in, or null
 * when none of them do. Same precedence as `signerRoster.ts`'s
 * `signerInstanceCount` — the first of name/lastName/email/phone that
 * resolves into a repeatable section — so "Auto" (no fixed instance) and a
 * pinned instance agree on which section they're talking about.
 */
export function repeatableSectionForMappings(
  mappings: {
    nameFieldId?: string | null;
    lastNameFieldId?: string | null;
    emailFieldId?: string | null;
    phoneFieldId?: string | null;
  } | null,
  sectionsByFieldId: Map<string, FormSection>,
): FormSection | null {
  if (!mappings) return null;
  for (const id of [
    mappings.nameFieldId,
    mappings.lastNameFieldId,
    mappings.emailFieldId,
    mappings.phoneFieldId,
  ]) {
    if (!id) continue;
    const section = sectionsByFieldId.get(id);
    if (section?.repeatable) return section;
  }
  return null;
}

export const NAME_FIELD_TYPES = NAME_TYPES;
export const EMAIL_FIELD_TYPES = EMAIL_TYPES;
export const PHONE_FIELD_TYPES = PHONE_TYPES;
export const LANGUAGE_FIELD_TYPES = LANGUAGE_TYPES;

export type PdfEntryChoice = {
  id: string;
  stepId: string;
  stepLabel: string;
  label: string;
};

/** Flatten every PDF source template into a single picker list. */
export function collectPdfEntries(template: WorkflowTemplate, isFr: boolean): PdfEntryChoice[] {
  const out: PdfEntryChoice[] = [];
  for (const step of stepsWithPdfTemplates(template)) {
    const stepLabel = localized(step.titleEn, step.titleFr, isFr);
    for (const e of step.fileEntries) {
      out.push({ id: e.id, stepId: step.id, stepLabel, label: e.label || stepLabel });
    }
  }
  return out;
}

/** Build a stable display label for one field choice. */
export function describeField(choice: FieldChoice): string {
  return `${choice.stepLabel} › ${choice.sectionLabel} › ${choice.fieldLabel}`;
}

/**
 * Walk a step's `oneSpanConfig` and return a quick `{signers, placements}`
 * count so the originator sees configuration depth on the step card without
 * opening the drawer. Used by `WorkflowSteps` for the chip next to the title.
 */
export function summariseOneSpanConfig(step: WorkflowStep): {
  signers: number;
  placements: number;
} {
  const cfg = step.oneSpanConfig;
  if (!cfg) return { signers: 0, placements: 0 };
  const placements = cfg.documents.reduce((sum, d) => sum + d.fields.length, 0);
  return { signers: cfg.roles.length, placements };
}
